import { api } from "@/lib/api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import { useState } from "react";
import { ResetSection } from "./settings-ui";

// ============================================================
// Danger Zone — Bookings Reset + Full Reset (self-contained)
// ============================================================

export function DangerZoneCard() {
	const queryClient = useQueryClient();
	const [confirmReset, setConfirmReset] = useState<
		"bookings" | "full" | null
	>(null);

	const bookingsResetMutation = useMutation({
		mutationFn: () =>
			api.post<{
				data: { ok: boolean; deleted: Record<string, number>; message: string };
			}>("/settings/sync/bookings/reset"),
		onSuccess: () => {
			setConfirmReset(null);
			queryClient.invalidateQueries();
		},
	});

	const fullResetMutation = useMutation({
		mutationFn: () =>
			api.post<{
				data: { ok: boolean; deleted: Record<string, number>; message: string };
			}>("/settings/sync/full-reset"),
		onSuccess: () => {
			setConfirmReset(null);
			queryClient.invalidateQueries();
		},
	});

	return (
		<div className="card border-red-200 bg-red-50/30 space-y-3">
			<div className="flex items-center gap-2">
				<AlertTriangle size={18} className="text-red-500" />
				<span className="font-medium text-red-800">Gefahrenzone</span>
			</div>
			<p className="text-xs text-stone-500">
				Diese Aktionen löschen Daten unwiderruflich. Danach muss neu
				synchronisiert werden.
			</p>
			<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
				{/* Bookings Reset */}
				<div className="bg-white rounded-lg border border-red-100 p-3 space-y-2">
					<p className="text-sm font-medium text-stone-700">
						Buchungen zurücksetzen
					</p>
					<p className="text-xs text-stone-500">
						Löscht alle Buchungen, Kontakte, Firmen und verknüpfte Daten.
						E-Mails und Belege bleiben erhalten.
					</p>
					<ResetSection
						service="bookings"
						confirmReset={confirmReset}
						setConfirmReset={setConfirmReset}
						mutation={bookingsResetMutation}
						label="Alle Buchungen, Kontakte und Firmen löschen"
					/>
				</div>

				{/* Full Reset */}
				<div className="bg-white rounded-lg border border-red-100 p-3 space-y-2">
					<p className="text-sm font-medium text-stone-700">Komplett-Reset</p>
					<p className="text-xs text-stone-500">
						Löscht ALLE Daten: Buchungen, Kontakte, Firmen, E-Mails, Belege und
						Einstellungen. Kompletter Neustart.
					</p>
					<ResetSection
						service="full"
						confirmReset={confirmReset}
						setConfirmReset={setConfirmReset}
						mutation={fullResetMutation}
						label="⚠️ ALLE Daten unwiderruflich löschen"
					/>
				</div>
			</div>
		</div>
	);
}
